/**
 * Native eReader bridge (web -> iOS).
 *
 * Wires the bib page "Read Now" / "Download" CTAs to the native eReader when the
 * app is running inside the iOS WebView with the eContent capability enabled.
 * Contract owner: iOS Apps pod. See web-team-integration.md.
 *
 * Everything here is guarded for SSR (no `window`) and fails safe: if the native
 * bridge is absent the callers fall back to today's web behavior.
 */

import { useState, useEffect } from 'react';

export const ECONTENT_ACTIONS = Object.freeze({
  OPEN: 'open', // Read Now
  DOWNLOAD: 'download' // Download icon
});

const MESSAGE_VERSION = 1;

function getWindow() {
  return typeof window === 'undefined' ? null : window;
}

/**
 * True only when native has injected `window.BCNativeCapabilities.eContent`.
 * Deliberately NOT a `window.webkit` check — that exists in every WKWebView
 * regardless of app version.
 *
 * NOTE: this is a truthiness check — any non-zero/non-empty `eContent` value counts,
 * not literally `1`. Capability presence is the on/off gate; the per-message `version`
 * field in `sendEContentAction` is what guards payload compatibility over time.
 *
 * @returns {boolean}
 */
export function isNativeEContentAvailable() {
  const win = getWindow();
  return !!(win && win.BCNativeCapabilities && win.BCNativeCapabilities.eContent);
}

/**
 * The native message handler, or null. Present only once the app has installed
 * `window.webkit.messageHandlers.EContent` with a `postMessage` function.
 */
function getEContentHandler() {
  const win = getWindow();
  const handler = win && win.webkit && win.webkit.messageHandlers && win.webkit.messageHandlers.EContent;
  return handler && typeof handler.postMessage === 'function' ? handler : null;
}

/**
 * Single source of truth for "can we hand off to native right now?". Requires BOTH
 * independent signals: the capability flag (`BCNativeCapabilities.eContent`) AND the
 * installed message handler. The label gate and the action gate share this so they
 * can't drift — i.e. we never render "Read now" unless a tap will actually hand off.
 *
 * @returns {boolean}
 */
export function isEContentReady() {
  return isNativeEContentAvailable() && !!getEContentHandler();
}

/**
 * SSR-safe React hook for capability-gating UI (e.g. the "Read now" vs "Access this
 * item" label). Returns `false` during SSR and the first client render so hydration
 * matches the server and web output is unchanged, then resolves to the real value
 * after mount. Gated on the same `isEContentReady` the action uses, so the label
 * only promises native when a tap will actually hand off. Web output stays unchanged.
 *
 * @returns {boolean}
 */
export function useIsNativeEContentAvailable() {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    setAvailable(isEContentReady());
  }, []);
  return available;
}

/**
 * Post a one-way message to the native eContent handler.
 *
 * @param {'open'|'download'} action
 * @param {string} econtentId  the catalog id (same id used everywhere else)
 * @param {{ format?: string, title?: string }} [meta]
 * @returns {boolean} true if handed off to native; false if it should fall back to web
 */
export function sendEContentAction(action, econtentId, { format, title } = {}) {
  if (!isEContentReady()) return false;

  if (action !== ECONTENT_ACTIONS.OPEN && action !== ECONTENT_ACTIONS.DOWNLOAD) return false;

  const trimmedId = typeof econtentId === 'string' ? econtentId.trim() : '';
  if (!trimmedId) return false;

  const handler = getEContentHandler(); // non-null: guaranteed by isEContentReady() above

  try {
    handler.postMessage({
      version: MESSAGE_VERSION,
      action,
      econtentId: trimmedId,
      format, // optional hint; native re-resolves authoritatively
      title // optional; analytics + toast copy only
    });
    return true;
  } catch (error) {
    // postMessage is one-way and must never throw into the UI.
    return false;
  }
}

/**
 * Register a handler that native calls with download state updates.
 * Only relevant for `action: "download"`. Safe to skip entirely (icon state can
 * instead be refreshed from the backend on next page load).
 *
 * First registration wins: if a handler is already registered we do NOT silently
 * overwrite it (which would drop the earlier consumer). The caller gets `false` back
 * so it knows its handler wasn't installed.
 *
 * @param {(event: { econtentId: string, status: string, progress?: number }) => void} handler
 * @returns {boolean} true if registered; false if invalid env/handler or one is already registered
 */
export function registerEContentEventHandler(handler) {
  const win = getWindow();
  if (!win || typeof handler !== 'function') return false;
  win.BCNative = win.BCNative || {};
  if (typeof win.BCNative.dispatchEContentEvent === 'function') return false;
  win.BCNative.dispatchEContentEvent = handler;
  return true;
}
